import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';
import { Tooltip } from './Tooltip';

type Variant = 'primary' | 'ghost' | 'on-dark' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

// DESIGN §5 Buttons: 6px radius (never pill), transform+background transitions, ease-out-quart.
const base =
  'inline-flex items-center justify-center gap-xs rounded-sm font-sans text-small font-medium ' +
  'transition-[background,transform] duration-almanac ease-almanac select-none ' +
  'focus-visible:shadow-focus disabled:opacity-40 disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  // Primary: Accent Blue → hover Ink Deep (no transform on hover, per DESIGN).
  primary: 'bg-accent-blue text-canvas-paper hover:bg-ink-deep',
  // Ghost: Canvas Paper, Accent Blue border+text → hover Canvas Sand + Ink Deep text.
  ghost:
    'bg-canvas-paper text-accent-blue border border-accent-blue hover:bg-canvas-sand hover:text-ink-deep',
  // On-Dark: for navy/accent surfaces.
  'on-dark': 'bg-canvas-paper text-ink-deep hover:bg-canvas-sand',
  // Danger: the single sanctioned error red, used for destructive confirms (PDPL erasure etc.).
  danger: 'bg-signal-error text-canvas-paper hover:opacity-90',
};

const sizes: Record<Size, string> = {
  sm: 'px-sm py-xs',
  md: 'px-lg py-[12px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-[14px] w-[14px] animate-spin rounded-pill border-2 border-current border-e-transparent"
    />
  );
}

/**
 * Action button gated by permission (spec §4): when not allowed it is DISABLED (not hidden)
 * and explains the missing permission via tooltip.
 */
export function PermissionButton({
  allowed,
  reason,
  children,
  disabled,
  ...rest
}: ButtonProps & { allowed: boolean; reason?: string | null }) {
  const btn = (
    <Button disabled={disabled || !allowed} {...rest}>
      {children}
    </Button>
  );
  if (allowed || !reason) return btn;
  // Wrap in a span so the tooltip works on a disabled button.
  return (
    <Tooltip content={reason}>
      <span className="inline-flex">{btn as ReactNode}</span>
    </Tooltip>
  );
}
